/**
 * Qué páginas se leyeron, cuáles no, y pedir más (GKTIBO-136).
 *
 * Con TASK-09 las veinte se eligen mejor, pero la elección sigue siendo
 * nuestra, y el que sabe qué páginas importan es el dueño. Esto le muestra la
 * decisión y lo deja corregirla. Dos cosas que este componente no hace:
 *
 * 1. **Mostrar nuestra jerga.** Los motivos llegan crudos del API y acá se
 *    traducen (D6); el test lo verifica sobre el texto en pantalla.
 * 2. **Dejar pedir de más.** El tope es el del trabajo (`HASTA_POR_VEZ`)
 *    y se aplica en las casillas, no sólo al mandar: un pedido que el API va
 *    a rechazar no debería poder armarse.
 */

import type { MotivoDeDescarte, PaginaLeidaResumen } from '@chatbot/shared-types';
import { useState } from 'react';
import { Button } from '../../components/ui/Button';
import { esPedible } from './motivos-web';
import { paginas as textos } from './textos-web';

/**
 * Cuántas se pueden pedir por vez. Es `HASTA_POR_VEZ` de
 * `@chatbot/shared-types`, copiado: el panel sólo importa TIPOS de ese
 * paquete (rompe `vite build`, ver `shared-types-boundary.test.ts`). El test
 * del componente compara los dos números para que no se separen.
 */
export const HASTA_POR_VEZ = 20;

interface Descartada {
  url: string;
  motivo: MotivoDeDescarte;
}

interface Props {
  leidas: PaginaLeidaResumen[];
  descartadas: Descartada[];
  /** Quedó sitio sin leer. Sin esto no hay nada que pedir, y no hay botón. */
  truncado: boolean;
  onPedir: (urls: string[]) => void;
  pidiendo?: boolean;
}

/** El título si lo hay; si no, la ruta. Nunca la URL entera: es ruido. */
function nombreDe(p: { url: string; titulo?: string }): string {
  if (p.titulo) return p.titulo;
  try {
    const u = new URL(p.url);
    return u.pathname === '/' ? u.hostname : u.pathname;
  } catch {
    return p.url;
  }
}

/**
 * Las que cuentan el negocio («tope») van marcadas de entrada, hasta el
 * máximo; el blog, no. Es la elección que el rastreo habría hecho con más
 * lugar, y el dueño la corrige tocando casillas.
 */
function eleccionInicial(descartadas: Descartada[]): Set<string> {
  return new Set(
    descartadas
      .filter((d) => d.motivo === 'tope')
      .slice(0, HASTA_POR_VEZ)
      .map((d) => d.url),
  );
}

export function PaginasDelSitio({ leidas, descartadas, truncado, onPedir, pidiendo }: Props) {
  const [elegidas, setElegidas] = useState(() => eleccionInicial(descartadas));
  const hayQuePedir = truncado && descartadas.some(esPedible);
  const llena = elegidas.size >= HASTA_POR_VEZ;

  function alternar(url: string) {
    setElegidas((antes) => {
      const ahora = new Set(antes);
      if (ahora.has(url)) ahora.delete(url);
      else if (ahora.size < HASTA_POR_VEZ) ahora.add(url);
      return ahora;
    });
  }

  return (
    <div className="alta-web-paginas">
      {leidas.length > 0 && (
        <details className="alta-web-grupo" data-testid="alta-web-leidas">
          <summary className="alta-web-grupo-titulo">{textos.queLeimos(leidas.length)}</summary>
          <ul className="alta-web-lista">
            {leidas.map((p) => (
              <li key={p.url} title={p.url}>
                {nombreDe(p)}
              </li>
            ))}
          </ul>
        </details>
      )}

      {descartadas.length > 0 && (
        <details className="alta-web-grupo" data-testid="alta-web-descartadas">
          <summary className="alta-web-grupo-titulo">
            {textos.queDejamosAfuera(descartadas.length)}
          </summary>
          {hayQuePedir && <p className="alta-web-ayuda">{textos.elegi(HASTA_POR_VEZ)}</p>}
          <ul className="alta-web-lista">
            {descartadas.map((d) => {
              const marcada = elegidas.has(d.url);
              return (
                <li key={d.url} title={d.url}>
                  {hayQuePedir && esPedible(d) ? (
                    <label className="alta-web-casilla">
                      <input
                        type="checkbox"
                        value={d.url}
                        checked={marcada}
                        // Con el tope lleno, las demás se apagan: un pedido
                        // que el API va a rechazar no se puede armar.
                        disabled={pidiendo || (!marcada && llena)}
                        onChange={() => alternar(d.url)}
                      />
                      <span>{nombreDe(d)}</span>
                      <span className="alta-web-motivo">{textos.motivos[d.motivo]}</span>
                    </label>
                  ) : (
                    <>
                      <span>{nombreDe(d)}</span>
                      <span className="alta-web-motivo">{textos.motivos[d.motivo]}</span>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
          {hayQuePedir && (
            <div className="alta-web-acciones">
              <Button
                variant="secondary"
                disabled={pidiendo || elegidas.size === 0}
                onClick={() => {
                  if (pidiendo || elegidas.size === 0) return;
                  onPedir([...elegidas]);
                }}
              >
                {pidiendo ? textos.leyendo : textos.leerEstas(elegidas.size)}
              </Button>
            </div>
          )}
        </details>
      )}
    </div>
  );
}
